import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { summarizeCareScenario } from '@shared/consumerPlan';
import { useClientData } from '@/hooks/useClientData';
import {
  type AppState,
  fetchClientByClientIdRequest,
  selectClient,
  selectClientLoadError,
} from '@/model';

/**
 * The signed-in consumer's client record with its care plan summarised for the flow. A failed load
 * is reported as `isError` (and `status: 'failed'`) with `retry` instead of an endless spinner.
 */
export function useConsumerClient(clientId: string) {
  const dispatch = useDispatch();
  const { client: maybeClient, isLoading } = useClientData();
  const isLoaded = maybeClient?.clientId === clientId;
  const client = useSelector((state: AppState) => (isLoaded ? selectClient(state) : null));
  const loadError = useSelector(selectClientLoadError);
  const scenario = useMemo(() => (client ? summarizeCareScenario(client) : null), [client]);
  const retry = useCallback(() => {
    dispatch(fetchClientByClientIdRequest(clientId));
  }, [dispatch, clientId]);

  const isError = !isLoaded && !!loadError;
  const isPending = (isLoading || !isLoaded) && !isError;
  const status = isError ? 'failed' : isPending ? 'loading' : 'ready';
  return { client, scenario, status, isLoading: isPending, isError, retry } as const;
}
